/**
 * Leak detection and the one-click leak fix for the test-pose gallery.
 *
 * A "leak" is a vertex that visibly moves under a test pose although it belongs to a part of the
 * body that pose does not rotate — the classic auto-skinning failure where the arm drags the
 * chest with it. The rule is deliberately conservative: it only accuses vertices that are
 * clearly on the static side of the boundary, so a legitimate soft blend across a joint is never
 * reported.
 *
 * Pure module — no DOM, no editor state, and no dependency on the voxel/geodesic or solver
 * modules (the caller supplies the cache and a re-solve callback).
 */

import { AppError } from '../../shared/errors';
import type { CanonicalBoneId, SkinData, SkinParams } from '../../shared/types';
import { jointParamsOf, mirrorBoneId, setJointParams } from './skinParams';
import type { LeakReport, TestPose, VertexDistanceCache } from './types';

/**
 * A vertex must move more than this (metres) before it can be called leaked.
 *
 * Absolute, and therefore only meaningful on a metre-scale model (audit §2.4 #3). On a
 * centimetre-scale one it is 0.05 mm of the model and practically every vertex trips it, so a
 * report of "everything leaks" is far more likely to be a unit mismatch than a skinning failure.
 * `blendZone` below is a fraction of `cache.extent` and does scale — the asymmetry is deliberate
 * (amendment A3), not an oversight. `checkSkinModelScale` in `skinParams.ts` is what warns about
 * the mismatch; the threshold itself is not rescaled here because `opts.threshold` and the
 * persisted `SkinParams` it sits beside would then mean something different per model.
 */
const DEFAULT_THRESHOLD = 0.005;

/** Blend-zone half width as a fraction of the mesh extent (amendment A3). */
const DEFAULT_BLEND_ZONE_FRACTION = 0.02;

const DEFAULT_FIX_STEP = 0.005;
const DEFAULT_FIX_MAX_STEPS = 10;

export interface DetectLeaksOptions {
  /** Displacement (metres) above which a vertex counts as moved. Default 0.005. */
  threshold?: number;
  /**
   * Vertices whose distance to the moved bones exceeds their distance to the static bones by
   * less than this (metres) sit in the legitimate blend zone and are never reported.
   * Default `0.02 * cache.extent`.
   */
  blendZone?: number;
}

export interface FixLeaksOptions {
  /** Metres subtracted from each moved bone's `offset` per iteration. Default 0.005. */
  step?: number;
  /** Hard iteration cap. Default 10. */
  maxSteps?: number;
}

/**
 * Vertex `v` (unique position `u = cache.uniqueOf[v]`) leaks under `pose` iff **all** of:
 * 1. `|deformed(v) − rest(v)| > threshold`;
 * 2. its nearest bone by cached geodesic distance is NOT in `pose.movedBones` (all-`Infinity`
 *    distances ⇒ not leaked — the solver had to fall back for that vertex anyway);
 * 3. `dMoved(u) − dStatic(u) > blendZone`, where `dMoved` is the smallest cached distance to any
 *    bone in `pose.movedBones` and `dStatic` the smallest to any bone outside it
 *    (`dStatic === Infinity` ⇒ not leaked).
 *
 * `leaked` is ascending; `maxDisplacement` is the largest displacement among the leaked vertices
 * (0 when there are none).
 */
export function detectLeaks(
  cache: VertexDistanceCache,
  skin: SkinData,
  restPositions: ArrayLike<number>,
  deformed: Float32Array,
  pose: TestPose,
  opts: DetectLeaksOptions = {},
): LeakReport {
  const vertexCount = cache.vertexCount;
  if (skin.vertexCount !== vertexCount) {
    throw new AppError(
      'SKIN_DATA_MISMATCH',
      `Skin data for mesh "${skin.meshName}" covers ${skin.vertexCount} vertices but the ` +
        `distance cache for "${cache.meshName}" has ${vertexCount}. Re-generate the skin weights.`,
      { skinMesh: skin.meshName, cacheMesh: cache.meshName, skin: skin.vertexCount, cache: vertexCount },
    );
  }
  if (restPositions.length < vertexCount * 3 || deformed.length < vertexCount * 3) {
    throw new AppError(
      'SKIN_DATA_MISMATCH',
      `Leak check for mesh "${cache.meshName}" needs ${vertexCount * 3} rest and deformed ` +
        `coordinates but got ${restPositions.length} / ${deformed.length}.`,
      { meshName: cache.meshName, vertexCount },
    );
  }

  const threshold = finiteOr(opts.threshold, DEFAULT_THRESHOLD);
  const blendZone = finiteOr(opts.blendZone, DEFAULT_BLEND_ZONE_FRACTION * cache.extent);

  const { uniqueCount, boneIds, distances } = cache;
  const moved = new Set<CanonicalBoneId>(pose.movedBones);

  // Per unique position: nearest bone, and the closest moved / static bone distances.
  const nearest = new Int32Array(uniqueCount).fill(-1);
  const dMoved = new Float64Array(uniqueCount).fill(Infinity);
  const dStatic = new Float64Array(uniqueCount).fill(Infinity);
  const dNearest = new Float64Array(uniqueCount).fill(Infinity);

  for (let b = 0; b < boneIds.length; b += 1) {
    const isMoved = moved.has(boneIds[b]);
    const base = b * uniqueCount;
    for (let u = 0; u < uniqueCount; u += 1) {
      const d = distances[base + u];
      if (!Number.isFinite(d)) continue;
      if (d < dNearest[u]) {
        dNearest[u] = d;
        nearest[u] = b;
      }
      if (isMoved) {
        if (d < dMoved[u]) dMoved[u] = d;
      } else if (d < dStatic[u]) {
        dStatic[u] = d;
      }
    }
  }

  const leaked: number[] = [];
  let maxDisplacement = 0;

  for (let v = 0; v < vertexCount; v += 1) {
    const p = v * 3;
    const dx = deformed[p] - restPositions[p];
    const dy = deformed[p + 1] - restPositions[p + 1];
    const dz = deformed[p + 2] - restPositions[p + 2];
    const disp = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (!(disp > threshold)) continue;

    const u = cache.uniqueOf[v];
    if (u < 0 || u >= uniqueCount) continue;

    const nearestBone = nearest[u];
    if (nearestBone < 0) continue; // every distance unreachable — not our call to make
    if (moved.has(boneIds[nearestBone])) continue; // legitimately carried by the pose

    const ds = dStatic[u];
    const dm = dMoved[u];
    if (!Number.isFinite(ds)) continue;
    if (!(dm - ds > blendZone)) continue; // inside the blend zone: soft falloff is expected

    leaked.push(v);
    if (disp > maxDisplacement) maxDisplacement = disp;
  }

  leaked.sort((a, b) => a - b);

  return {
    poseId: pose.id,
    threshold,
    leaked,
    maxDisplacement,
    count: leaked.length,
  };
}

/**
 * Shrink the moved bones' regions until the leak count reaches zero.
 *
 * Each iteration subtracts `step` metres from every moved bone's `offset` and re-evaluates via
 * the caller's `evaluate` callback (which re-solves the weights and re-runs `detectLeaks`).
 * `setJointParams` already mirrors under `params.symmetry`, so a Left/Right pair is decremented
 * exactly once per iteration, never twice.
 *
 * Returns the BEST (lowest leak count) params/report seen — including the untouched input when
 * no iteration improved on it. Stops early once `setJointParams` stops changing the offsets
 * (they are clamped to ±0.1 m).
 */
export function fixLeaks(
  cache: VertexDistanceCache,
  params: SkinParams,
  pose: TestPose,
  evaluate: (params: SkinParams) => LeakReport,
  opts: FixLeaksOptions = {},
): { params: SkinParams; report: LeakReport } {
  const step = Math.abs(finiteOr(opts.step, DEFAULT_FIX_STEP));
  const maxSteps = Math.max(0, Math.floor(finiteOr(opts.maxSteps, DEFAULT_FIX_MAX_STEPS)));

  const targets = adjustableBones(cache, params, pose);

  let current = params;
  let report = evaluate(current);
  let best = { params: current, report };

  for (let i = 0; i < maxSteps && report.count > 0; i += 1) {
    let next = current;
    let changed = false;
    for (const bone of targets) {
      const before = jointParamsOf(next, bone).offset;
      next = setJointParams(next, bone, { offset: before - step });
      if (jointParamsOf(next, bone).offset !== before) changed = true;
    }
    if (!changed) break; // offsets clamped out — more iterations cannot help

    current = next;
    report = evaluate(current);
    if (report.count < best.report.count) best = { params: current, report };
  }

  return best;
}

// ---------------------------------------------------------------------------
// internals
// ---------------------------------------------------------------------------

/**
 * The bones `fixLeaks` decrements: the pose's moved bones that the cache actually carries, with
 * one representative per Left/Right pair while `params.symmetry` is on (`setJointParams` writes
 * the mirror itself). Falls back to the raw list if the cache names none of them.
 */
function adjustableBones(
  cache: VertexDistanceCache,
  params: SkinParams,
  pose: TestPose,
): CanonicalBoneId[] {
  const known = new Set<CanonicalBoneId>(cache.boneIds);
  const inCache = pose.movedBones.filter((b) => known.has(b));
  const candidates = inCache.length > 0 ? inCache : [...pose.movedBones];
  if (!params.symmetry) return candidates;

  const out: CanonicalBoneId[] = [];
  const claimed = new Set<CanonicalBoneId>();
  for (const bone of candidates) {
    if (claimed.has(bone)) continue;
    claimed.add(bone);
    claimed.add(mirrorBoneId(bone));
    out.push(bone);
  }
  return out;
}

function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
